/**
 * The comments icon in a document's header (RFC 0324 3e).
 *
 * The column opens from here. The icon says how many threads are open, whose
 * they are, and, with a yellow dot, that one is waiting on you. It replaces
 * reading the count off a pill inside the editor.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  type CommentAuthor,
  isWaitingOn,
  projectAuthor,
  projectThreads,
} from '@mod/core/model'
import { MessageSquare } from 'lucide-react'
import { useMemo } from 'react'
import { useCommentMap } from '../../services/comments/useCommentMap'
import { useLocalPersonId } from '../../services/identity/localPerson'
import { sessionFace } from '../../services/sessions/sessionFace'
import { useDocument } from '../../services/world/repo/useDocument'

export function DocumentCommentsButton({
  projectId,
  documentId,
  active,
  onToggle,
}: {
  projectId: string
  documentId: string
  active: boolean
  onToggle: () => void
}) {
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  // The page's comments where they live (RFC 0339).
  const commentMap = useCommentMap(
    projectId,
    useMemo(() => [documentId], [documentId]),
  )
  const me = useLocalPersonId()
  const { open, faces, waiting } = useMemo(() => {
    const threads = projectThreads(commentMap, { documentId }).filter(
      (t) => !t.root.resolved,
    )
    const seen = new Set<string>()
    const faces: CommentAuthor[] = []
    for (const t of threads) {
      const a = sessionFace(projectAuthor(projectDoc, t.root))
      // By session when there is one: two sessions both still called
      // "Session 3" are two faces, not one.
      const key = a.sessionId ?? a.label
      if (seen.has(key)) continue
      seen.add(key)
      faces.push(a)
      if (faces.length === 2) break
    }
    return {
      open: threads.length,
      faces,
      waiting: threads.some((t) => isWaitingOn(t, me)),
    }
  }, [projectDoc, commentMap, documentId, me])

  const title =
    open === 0
      ? 'Comments'
      : `${open} open comment${open === 1 ? '' : 's'}${waiting ? ', one waiting on you' : ''}`
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      onClick={onToggle}
      className={`relative flex h-6 items-center gap-1.5 rounded-md px-1.5 text-[12px] ${
        active
          ? 'bg-muted text-foreground'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      }`}
    >
      <MessageSquare size={14} />
      {faces.length > 0 && (
        <span className="flex">
          {faces.map((f, i) => (
            <span
              key={f.label}
              className={`flex size-4 items-center justify-center overflow-hidden rounded-full font-semibold text-[8px] text-white ring-1 ring-background ${
                i > 0 ? '-ml-1.5' : ''
              }`}
              style={{ background: f.avatarUrl ? undefined : f.tint }}
            >
              {f.avatarUrl ? (
                <img
                  src={f.avatarUrl}
                  alt=""
                  className="size-full object-cover"
                />
              ) : (
                f.initials
              )}
            </span>
          ))}
        </span>
      )}
      {open > 0 && <span className="font-medium tabular-nums">{open}</span>}
      {waiting && (
        <span className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-yellow-500" />
      )}
    </button>
  )
}
